// BillingPage.js
import React, { useState, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Select from "react-select";
import { Input, message } from "antd";
import moment from "moment";
import { CreditCard, Trash2, PlusCircle,ChevronUp,ChevronDown } from "react-feather";
import TableManagement from "./TableManagement";
import { usePosContext } from "../../context/PosContext"; // Ensure this import path is correct

const BillingPage = () => {
  const { tables, rooms, settleTable, settleRoom, setRooms } = usePosContext();
  const [customerPhone, setCustomerPhone] = useState("");
  const [paymentMode, setPaymentMode] = useState(null);
  const [taxIncluded, setTaxIncluded] = useState(true);
  const [orderSource, setOrderSource] = useState({ value: "Dine-In", label: "Dine-In" });
  const [discountPercentage, setDiscountPercentage] = useState(0);
  const [isFooterExpanded, setIsFooterExpanded] = useState(false);
  const [isPartialPaymentEnabled, setIsPartialPaymentEnabled] = useState(false);
  const [partialPayments, setPartialPayments] = useState([]);
  const [currentPartialMethod, setCurrentPartialMethod] = useState("Cash");
  const [currentPartialAmount, setCurrentPartialAmount] = useState("");
  const [isComplementary, setIsComplementary] = useState(false);
  const [selectedTable, setSelectedTable] = useState(null);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [isRoomCredit, setIsRoomCredit] = useState(false);
  const printIframeRef = useRef(null);

  const paymentOptions = [
    { value: "Cash", label: "Cash" },
    { value: "UPI", label: "UPI" },
    { value: "Card", label: "Card" },
    { value: "Due", label: "Due" },
  ];

  const sampleTaxes = [
    { tax_id: "tax1", name: "VAT", rate: 5.0 },
    { tax_id: "tax2", name: "Sales Tax", rate: 8.0 },
  ];

  const sourceOptions = [
    { value: "Dine-In", label: "Dine-In" },
    { value: "Takeaway", label: "Takeaway" },
    { value: "Online", label: "Online" },
    { value: "Zomato", label: "Zomato" },
    { value: "Swiggy", label: "Swiggy" },
    { value: "Room Service", label: "Room Service" },
  ];

  // Fetch KOT items for the selected table or room
  const kotItems = useMemo(() => {
    if (selectedTable) {
      const table = tables.find((t) => t.id === selectedTable);
      return table ? table.kots.flatMap((kot) => kot.items) : [];
    } else if (selectedRoom) {
      const room = rooms.find((r) => r.id === selectedRoom);
      return room ? room.kots.flatMap((kot) => kot.items) : [];
    }
    return [];
  }, [selectedTable, selectedRoom, tables, rooms]);

  const { subtotal, taxTotal, grandTotal } = useMemo(() => {
    const sub = kotItems.reduce((sum, item) => sum + item.amount, 0);
    const tax = kotItems.reduce((sum, item) => {
      if (item.is_taxable) {
        const taxRate = sampleTaxes.find((t) => t.tax_id === item.tax_id)?.rate || 0;
        return sum + (item.amount * taxRate) / 100;
      }
      return sum;
    }, 0);
    const baseTotal = taxIncluded ? sub + tax : sub;
    return {
      subtotal: sub,
      taxTotal: tax,
      grandTotal: isComplementary ? 0 : baseTotal,
    };
  }, [kotItems, taxIncluded, isComplementary]);

  const handleGenerateBill = () => {
    if (!kotItems.length) {
      message.error("No KOT items to bill! Please generate a KOT first.");
      return;
    }

    const totalAmount = isComplementary ? 0 : grandTotal * (1 - discountPercentage / 100);
    const paidAmount = partialPayments.reduce((sum, payment) => sum + (payment.amount || 0), 0);

    if (paymentMode && !isPartialPaymentEnabled && !isRoomCredit) {
      const order = {
        order_id: `CSM-${Date.now().toString().slice(-4)}`,
        company_id: 1,
        customer_phone: customerPhone || null,
        order_type: selectedRoom ? "Room Service" : "Dine-In",
        source: orderSource.value,
        status: "Completed",
        is_settled: true,
        billing_type: "Direct",
        tax_amount: taxTotal,
        created_by: "user1",
        business_date: moment().format("YYYY-MM-DD"),
        items: kotItems,
        payment_method: paymentMode.value,
        discount_percentage: discountPercentage,
        partial_payments: [],
        is_complementary: isComplementary,
        table_id: selectedTable,
        room_id: selectedRoom,
      };
      printBill(order);
    } else if (partialPayments.length > 0) {
      if (paidAmount < totalAmount && !partialPayments.some(p => p.method === "Due")) {
        message.error("Please cover the full amount or mark the remaining as 'Due'!");
        return;
      }
      const order = {
        order_id: `CSM-${Date.now().toString().slice(-4)}`,
        company_id: 1,
        customer_phone: customerPhone || null,
        order_type: selectedRoom ? "Room Service" : "Dine-In",
        source: orderSource.value,
        status: "Completed",
        is_settled: paidAmount >= totalAmount,
        billing_type: "Direct",
        tax_amount: taxTotal,
        created_by: "user1",
        business_date: moment().format("YYYY-MM-DD"),
        items: kotItems,
        payment_method: "Partial",
        discount_percentage: discountPercentage,
        partial_payments: partialPayments,
        is_complementary: isComplementary,
        table_id: selectedTable,
        room_id: selectedRoom,
      };
      printBill(order);
    } else if (isRoomCredit && selectedRoom) {
      const order = {
        order_id: `CSM-${Date.now().toString().slice(-4)}`,
        company_id: 1,
        customer_phone: customerPhone || null,
        order_type: "Room Service",
        source: orderSource.value,
        status: "Completed",
        is_settled: false,
        billing_type: "Room Credit",
        tax_amount: taxTotal,
        created_by: "user1",
        business_date: moment().format("YYYY-MM-DD"),
        items: kotItems,
        payment_method: "Room Credit",
        discount_percentage: discountPercentage,
        partial_payments: [],
        is_complementary: isComplementary,
        table_id: selectedTable,
        room_id: selectedRoom,
      };
      printBill(order);
    } else {
      message.error("Please select a payment mode, add partial payments, or credit to room!");
      return;
    }
  };

  const printBill = (order) => {
    const totalAmount = order.is_complementary ? 0 : grandTotal * (1 - discountPercentage / 100);
    const paidAmount = order.partial_payments.reduce((sum, payment) => sum + (payment.amount || 0), 0);

    const printContent = `
      <html>
      <head>
        <title>Counter Bill</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 0; padding: 0; font-size: 12px; }
          .bill { max-width: 300px; margin: 10px auto; padding: 15px; border: 1px dashed #ddd; border-radius: 5px; }
          .bill-header { text-align: center; margin-bottom: 15px; }
          .bill-header h2 { font-size: 18px; margin: 0 0 8px 0; }
          .bill-header p { margin: 2px 0; font-size: 11px; color: #555; }
          .bill-table { width: 100%; border-collapse: collapse; margin-bottom: 15px; }
          .bill-table th, .bill-table td { border-bottom: 1px solid #eee; padding: 6px; text-align: left; font-size: 11px; }
          .bill-table th { background-color: #f8f9fa; font-weight: 600; }
          .bill-total { margin-top: 10px; text-align: right; }
          .bill-total p { margin: 4px 0; font-size: 12px; font-weight: 500; }
          .bill-payments { margin-top: 10px; text-align: right; }
          .bill-payments p { margin: 4px 0; font-size: 11px; }
          .bill-footer { text-align: center; margin-top: 15px; font-size: 11px; color: #666; }
          @media print { .bill { border: none; } body { margin: 0; } }
        </style>
      </head>
      <body>
        <div className="bill">
          <div className="bill-header">
            <h2>City Super Market</h2>
            <p>No. 15, Block-1, 5th Floor, BG Shopping Complex</p>
            <p>Jayanagar, Bangalore</p>
            <p>Bill No.: ${order.order_id} | ${moment().format("DD-MM-YYYY HH:mm")}</p>
            ${order.table_id ? `<p>Table: ${order.table_id}</p>` : order.room_id ? `<p>Room: ${order.room_id}</p>` : ""}
          </div>
          <table className="bill-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Item</th>
                <th>Qty</th>
                <th>Rate</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              ${order.items
                .map((item, index) => `
                  <tr>
                    <td>${index + 1}</td>
                    <td>${item.name}</td>
                    <td>${item.qty}</td>
                    <td>₹${item.price_standard.toFixed(2)}</td>
                    <td>₹${item.amount.toFixed(2)}</td>
                  </tr>
                `)
                .join("")}
            </tbody>
          </table>
          <div className="bill-total">
            <p>Subtotal: ₹${subtotal.toFixed(2)}</p>
            <p>Tax: ₹${taxTotal.toFixed(2)}</p>
            ${discountPercentage > 0 ? `<p>Discount (${discountPercentage}%): -₹${(grandTotal * (discountPercentage / 100)).toFixed(2)}</p>` : ""}
            ${order.is_complementary ? `<p>Complementary: Yes</p>` : ""}
            <p style="font-weight: bold;">Grand Total: ₹${totalAmount.toFixed(2)}</p>
          </div>
          <div className="bill-payments">
            ${order.partial_payments.length > 0 ? `
              ${order.partial_payments.map(p => `
                <p>${p.method}: ₹${p.amount.toFixed(2)}</p>
              `).join("")}
              ${paidAmount < totalAmount ? `<p>Due: ₹${(totalAmount - paidAmount).toFixed(2)}</p>` : ""}
            ` : order.payment_method === "Room Credit" ? `<p>Charged to Room: ₹${totalAmount.toFixed(2)}</p>` : `<p>${order.payment_method}: ₹${totalAmount.toFixed(2)}</p>`}
            ${order.payment_method === "Cash" ? `<p>Balance: ₹0.00</p>` : ""}
          </div>
          <div className="bill-footer">
            <p>Thank You, Visit Again!</p>
          </div>
        </div>
        <script>
          window.print();
          window.close();
        </script>
      </body>
      </html>
    `;

    if (printIframeRef.current) {
      printIframeRef.current.contentDocument.write(printContent);
      printIframeRef.current.contentWindow.print();
      printIframeRef.current.contentDocument.close();
    } else {
      const printWindow = window.open("", "_blank");
      printWindow.document.write(printContent);
      printWindow.print();
      printWindow.close();
    }

    if (order.payment_method !== "Room Credit") {
      if (selectedTable) {
        settleTable(selectedTable);
        setSelectedTable(null);
      } else if (selectedRoom) {
        settleRoom(selectedRoom);
        setSelectedRoom(null);
      }
    } else {
      if (selectedRoom) {
        setRooms((prev) =>
          prev.map((room) =>
            room.id === selectedRoom ? { ...room, kots: [] } : room
          )
        );
      }
    }

    setCustomerPhone("");
    setPaymentMode(null);
    setOrderSource({ value: "Dine-In", label: "Dine-In" });
    setDiscountPercentage(0);
    setPartialPayments([]);
    setIsPartialPaymentEnabled(false);
    setCurrentPartialMethod("Cash");
    setCurrentPartialAmount("");
    setIsComplementary(false);
    setIsRoomCredit(false);
    message.success("Bill generated and printed successfully!");
  };

  const handleTogglePartialPayment = () => {
    setIsPartialPaymentEnabled(!isPartialPaymentEnabled);
    if (!isPartialPaymentEnabled) {
      setPartialPayments([]);
      setCurrentPartialMethod("Cash");
      setCurrentPartialAmount("");
    }
  };

  const handleAddPartialPayment = () => {
    const totalAmount = isComplementary ? 0 : grandTotal * (1 - discountPercentage / 100);
    const currentPaid = partialPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
    const newAmount = parseFloat(currentPartialAmount) || 0;

    if (newAmount <= 0) {
      message.error("Please enter a valid amount!");
      return;
    }
    if (currentPaid + newAmount > totalAmount) {
      message.error(`Amount exceeds total bill (₹${totalAmount.toFixed(2)})!`);
      return;
    }

    setPartialPayments((prev) => [...prev, { method: currentPartialMethod, amount: newAmount }]);
    setCurrentPartialAmount("");
  };

  const handleRemovePartialPayment = (index) => {
    setPartialPayments((prev) => prev.filter((_, i) => i !== index));
  };

  const itemCount = kotItems.length;

  return (
    <div className="page-wrapper">
      <div className="pos-counter-billing-page container-fluid h-100" style={{ padding: 0 }}>
        <div className="row h-100" style={{ margin: 0 }}>
          {/* Left Panel - Table/Room Selection */}
          <div className="col-md-12 col-lg-6 pos-menu-panel" style={{ height: "calc(100vh - 80px)", padding: "20px", background: "#f8f9fa", borderRadius: "12px" }}>
            <motion.div className="pos-menu-container" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
              <TableManagement
                onSelectTable={setSelectedTable}
                onSelectRoom={setSelectedRoom}
              />
            </motion.div>
          </div>

          {/* Right Panel - Billing */}
          <div className="col-md-12 col-lg-6 pos-billing-panel" style={{ 
            height: "100vh", 
            position: "fixed", 
            right: 0, 
            top: 0, 
            zIndex: 2000, 
            padding: "20px", 
            background: "#fff", 
            borderRadius: "12px", 
            boxShadow: "0 4px 12px rgba(0,0,0,0.05)", 
            borderTop: "5px solid #28a745"
          }}>
            <motion.div className="pos-bill-container" initial={{ x: 400, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ duration: 0.5 }}>
              {/* Invoice Header with Items Count and Source Selection */}
              <div className="pos-bill-header d-flex justify-content-between align-items-center">
                <span className="pos-item-count fw-bold" style={{ fontSize: "16px", color: "#212529" }}>{itemCount} Items</span>
                <h3 style={{ margin: 0, fontSize: "24px", fontWeight: "600", color: "#212529" }}>Invoice</h3>
                <Select
                  options={sourceOptions}
                  value={orderSource}
                  onChange={(option) => setOrderSource(option)}
                  placeholder="Select Source"
                  className="pos-source-select"
                  styles={{
                    control: (base) => ({
                      ...base,
                      borderRadius: "8px",
                      borderColor: "#ced4da",
                      fontSize: "14px",
                      height: "38px",
                      minWidth: "140px",
                      padding: "0 5px",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                    }),
                    menu: (base) => ({
                      ...base,
                      zIndex: 1001,
                    }),
                    singleValue: (base) => ({
                      ...base,
                      color: "#495057",
                      fontWeight: "500",
                    }),
                    indicatorsContainer: (base) => ({
                      ...base,
                      padding: "0 5px",
                    }),
                  }}
                />
              </div>

              {/* Bill Items with Bottom Border */}
              <div className="pos-bill-items" style={{ 
                height: "calc(100vh - 380px)", 
                overflowY: "auto", 
                margin: "15px 0 20px 0", 
                borderBottom: "5px solid #28a745"
              }}>
                {kotItems.length === 0 ? (
                  <div style={{ height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "#6c757d" }}>
                    <PlusCircle size={48} style={{ marginBottom: "15px", color: "#007bff" }} />
                    <span style={{ fontWeight: "500", fontSize: "16px" }}>Select a table or room with KOTs to generate a bill</span>
                  </div>
                ) : (
                  <AnimatePresence>
                    {kotItems.map((item, index) => (
                      <motion.div
                        key={`${item.menu_id}-${index}`}
                        className="pos-bill-item"
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                        style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px", borderBottom: "1px solid #eee" }}
                      >
                        <span className="pos-item-name" style={{ fontSize: "15px", fontWeight: "500", maxWidth: "40%", color: "#212529" }}>{item.name}</span>
                        <div className="pos-item-controls" style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                          <span style={{ fontSize: "14px", color: "#212529" }}>{item.qty}</span>
                          <span className="pos-item-amount" style={{ minWidth: "70px", textAlign: "right", fontSize: "14px", color: "#212529" }}>
                            ₹{item.amount.toFixed(2)}
                          </span>
                        </div>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                )}
              </div>

              {/* Summary Section */}
              <div className="pos-bill-summary">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  {/* Include Tax Toggle */}
                  <div className="d-flex align-items-center gap-2">
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        id="pos-tax-toggle"
                        checked={taxIncluded}
                        onChange={(e) => setTaxIncluded(e.target.checked)}
                        style={{ display: "none" }}
                      />
                      <span
                        style={{
                          display: "inline-block",
                          width: "40px",
                          height: "20px",
                          background: taxIncluded ? "#28a745" : "#ced4da",
                          borderRadius: "20px",
                          position: "relative",
                          cursor: "pointer",
                          transition: "background 0.3s ease",
                        }}
                      >
                        <span
                          style={{
                            position: "absolute",
                            top: "2px",
                            left: taxIncluded ? "20px" : "2px",
                            width: "16px",
                            height: "16px",
                            background: "white",
                            borderRadius: "50%",
                            transition: "left 0.3s ease",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                          }}
                        />
                      </span>
                    </label>
                    <label htmlFor="pos-tax-toggle" style={{ fontSize: "14px", color: "#212529", cursor: "pointer" }}>Include Tax</label>
                  </div>
                  {/* Complementary Toggle */}
                  <div className="d-flex align-items-center gap-2">
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        id="pos-complementary"
                        checked={isComplementary}
                        onChange={(e) => setIsComplementary(e.target.checked)}
                        style={{ display: "none" }}
                      />
                      <span
                        style={{
                          display: "inline-block",
                          width: "40px",
                          height: "20px",
                          background: isComplementary ? "#28a745" : "#ced4da",
                          borderRadius: "20px",
                          position: "relative",
                          cursor: "pointer",
                          transition: "background 0.3s ease",
                        }}
                      >
                        <span
                          style={{
                            position: "absolute",
                            top: "2px",
                            left: isComplementary ? "20px" : "2px",
                            width: "16px",
                            height: "16px",
                            background: "white",
                            borderRadius: "50%",
                            transition: "left 0.3s ease",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                          }}
                        />
                      </span>
                    </label>
                    <label htmlFor="pos-complementary" style={{ fontSize: "14px", color: "#212529", cursor: "pointer" }}>Complementary</label>
                  </div>
                </div>

                {/* Payment Method Selection */}
                <Select
                  options={paymentOptions}
                  value={paymentMode}
                  onChange={setPaymentMode}
                  placeholder="Select Payment Method"
                  className="pos-payment-select"
                  styles={{
                    control: (base) => ({
                      ...base,
                      borderRadius: "8px",
                      borderColor: "#ced4da",
                      fontSize: "14px",
                      height: "38px",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                    }),
                    menu: (base) => ({
                      ...base,
                      zIndex: 1001,
                    }),
                  }}
                />

                {/* Room Credit Option (for Room Service) */}
                {selectedRoom && (
                  <div style={{ display: "flex", alignItems: "center", gap: "5px", marginTop: "10px" }}>
                    <input
                      type="checkbox"
                      id="pos-room-credit"
                      checked={isRoomCredit}
                      onChange={(e) => setIsRoomCredit(e.target.checked)}
                      style={{ marginRight: "5px" }}
                    />
                    <label htmlFor="pos-room-credit" style={{ fontSize: "14px", color: "#212529" }}>
                      Credit to Room Account
                    </label>
                  </div>
                )}

                {/* Generate & Print Bill Button */}
                <button
                  className="pos-generate-btn btn btn-success w-100 mt-3"
                  onClick={handleGenerateBill}
                  disabled={!kotItems.length}
                  style={{
                    padding: "12px",
                    borderRadius: "8px",
                    background: !kotItems.length ? "#ced4da" : "#28a745",
                    color: "white",
                    border: "none",
                    fontSize: "16px",
                    cursor: !kotItems.length ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                    transition: "background 0.3s ease",
                  }}
                  onMouseEnter={(e) => !kotItems.length ? null : (e.target.style.background = "#218838")}
                  onMouseLeave={(e) => !kotItems.length ? null : (e.target.style.background = "#28a745")}
                >
                  <CreditCard size={18} /> Generate & Print Bill
                </button>

                {/* Expand/Collapse Footer */}
                <div className="pos-bill-footer-container">
                  <motion.div
                    className="pos-bill-footer d-flex justify-content-between align-items-center p-2 bg-white border-top shadow-sm"
                    initial={{ height: 50 }}
                    animate={{ height: isFooterExpanded ? 280 : 50 }}
                    transition={{ duration: 0.3 }}
                    style={{ marginTop: "15px", borderRadius: "8px" }}
                  >
                    {isFooterExpanded ? (
                      <div style={{ display: "flex", flexDirection: "column", gap: "10px", width: "100%" }}>
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                          <span style={{ fontSize: "14px", color: "#212529" }}>Subtotal:</span>
                          <span style={{ fontSize: "14px", color: "#212529" }}>₹{subtotal.toFixed(2)}</span>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                          <span style={{ fontSize: "14px", color: "#212529" }}>Tax ({taxIncluded ? "Included" : "Excluded"}):</span>
                          <span style={{ fontSize: "14px", color: "#212529" }}>₹{taxTotal.toFixed(2)}</span>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <span style={{ fontSize: "14px", color: "#212529" }}>Discount (%):</span>
                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={discountPercentage}
                            onChange={(e) => setDiscountPercentage(Math.min(100, Math.max(0, Number(e.target.value))))}
                            style={{ width: "60px", padding: "5px", borderRadius: "4px", border: "1px solid #ced4da", fontSize: "14px" }}
                          />
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", fontWeight: "600" }}>
                          <span style={{ fontSize: "14px", color: "#212529" }}>Grand Total:</span>
                          <span style={{ fontSize: "14px", color: "#212529" }}>₹{(grandTotal * (1 - discountPercentage / 100)).toFixed(2)}</span>
                        </div>

                        {/* Partial Payment */}
                        <label style={{ display: "flex", alignItems: "center", gap: "5px", marginTop: "10px" }}>
                          <input
                            type="checkbox"
                            id="pos-partial-payment"
                            checked={isPartialPaymentEnabled}
                            onChange={handleTogglePartialPayment}
                            style={{ display: "none" }}
                          />
                          <span
                            style={{
                              display: "inline-block",
                              width: "40px",
                              height: "20px",
                              background: isPartialPaymentEnabled ? "#28a745" : "#ced4da",
                              borderRadius: "20px",
                              position: "relative",
                              cursor: "pointer",
                              transition: "background 0.3s ease",
                            }}
                          >
                            <span
                              style={{
                                position: "absolute",
                                top: "2px",
                                left: isPartialPaymentEnabled ? "20px" : "2px",
                                width: "16px",
                                height: "16px",
                                background: "white",
                                borderRadius: "50%",
                                transition: "left 0.3s ease",
                                boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                              }}
                            />
                          </span>
                          <span style={{ fontSize: "14px", color: "#212529" }}>Partial Payment</span>
                        </label>
                        {isPartialPaymentEnabled && (
                          <div style={{ marginTop: "10px" }}>
                            <div style={{ display: "flex", gap: "10px", marginBottom: "10px" }}>
                              <Select
                                options={paymentOptions.filter(p => p.value !== "Due")}
                                value={paymentOptions.find(p => p.value === currentPartialMethod)}
                                onChange={(option) => setCurrentPartialMethod(option.value)}
                                styles={{ control: (base) => ({ ...base, width: "120px", height: "34px", fontSize: "14px" }) }}
                              />
                              <input
                                type="number"
                                value={currentPartialAmount}
                                onChange={(e) => setCurrentPartialAmount(e.target.value)}
                                placeholder="Amount"
                                step="0.01"
                                style={{ padding: "5px", borderRadius: "4px", border: "1px solid #ced4da", width: "100px", fontSize: "14px" }}
                              />
                              <button onClick={handleAddPartialPayment} style={{ padding: "5px 15px", borderRadius: "4px", background: "#007bff", color: "white", border: "none", fontSize: "14px" }}>
                                Add
                              </button>
                            </div>
                            {partialPayments.map((payment, index) => (
                              <div key={index} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "8px" }}>
                                <span style={{ fontSize: "14px", color: "#212529", lineHeight: "24px" }}>{payment.method}: ₹{payment.amount.toFixed(2)}</span>
                                <button 
                                  onClick={() => handleRemovePartialPayment(index)} 
                                  style={{ 
                                    padding: "4px 8px", 
                                    background: "#dc3545", 
                                    color: "white", 
                                    border: "none", 
                                    borderRadius: "4px", 
                                    display: "flex", 
                                    alignItems: "center", 
                                    justifyContent: "center",
                                    transition: "background 0.2s ease",
                                    marginLeft: "4px",
                                  }}
                                  onMouseEnter={(e) => (e.target.style.background = "#c82333")}
                                  onMouseLeave={(e) => (e.target.style.background = "#dc3545")}
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            ))}
                            {partialPayments.length > 0 && (
                              <div style={{ marginTop: "10px", textAlign: "right" }}>
                                <span style={{ fontSize: "14px", color: "#212529" }}>
                                  Due: ₹{((grandTotal * (1 - discountPercentage / 100)) - partialPayments.reduce((sum, p) => sum + (p.amount || 0), 0)).toFixed(2)}
                                </span>
                              </div>
                            )}
                          </div>
                        )}
                        <button
                          className="pos-footer-toggle btn btn-light btn-sm mt-auto expanded"
                          onClick={() => setIsFooterExpanded(!isFooterExpanded)}
                          style={{
                            alignSelf: "flex-end",
                            position: "absolute",
                            bottom: 10,
                            right: 10,
                            background: "#007bff",
                            color: "white",
                            border: "none",
                            borderRadius: "50%",
                            width: "36px",
                            height: "36px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                            transition: "background 0.3s ease",
                          }}
                          onMouseEnter={(e) => (e.target.style.background = "#0056b3")}
                          onMouseLeave={(e) => (e.target.style.background = "#007bff")}
                        >
                          <ChevronUp size={24} />
                        </button>
                      </div>
                    ) : (
                      <div className="d-flex justify-content-between align-items-center w-100">
                        <span className="pos-total-amount fw-bold text-success" style={{ fontSize: "20px", fontWeight: "700", color: "#28a745" }}>
                          Total ₹{(grandTotal * (1 - discountPercentage / 100)).toFixed(2)}
                        </span>
                        <button
                          className="pos-footer-toggle btn btn-light btn-sm"
                          onClick={() => setIsFooterExpanded(!isFooterExpanded)}
                          style={{
                            position: "relative",
                            right: 0,
                            background: "#007bff",
                            color: "white",
                            border: "none",
                            borderRadius: "50%",
                            width: "36px",
                            height: "36px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                            transition: "background 0.3s ease",
                          }}
                          onMouseEnter={(e) => (e.target.style.background = "#0056b3")}
                          onMouseLeave={(e) => (e.target.style.background = "#007bff")}
                        >
                          <ChevronDown size={24} />
                        </button>
                      </div>
                    )}
                  </motion.div>
                  <iframe ref={printIframeRef} title="print-iframe" style={{ display: "none" }} />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillingPage;